import React, { useEffect, useRef, useState } from 'react'
import { makeStyles, TableSortLabel } from '@material-ui/core'
import { ActivityIndicator, Text, View } from 'react-native'
import { SelectOption, jsFileDownload } from '@pulseops/common'
import { useForm } from 'react-hook-form'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import * as ExcelJS from 'exceljs'
import PDTable from './components/PDTable'
import PDStockInFilter from './components/PDStockInFilter'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import PDChangeInfoDialog from './components/PDChangeInfoDialog'
import { usePolicyDispatchingOffice } from './components/PolicyDispatchingOfficeContext'
import {
  PolicyDispatchingService,
  StockInPayload,
  StockInSearchItem,
  StockInSearchResponse
} from './services/PolicyDispatchingService'
import { getStatusLabel, getStatusStyle, getStatusValue } from './utils/policyDispatchingUtils'

type StockInFilterValues = {
  issueDate: Date | null
  contractNumber: string
  status: { label: string; value: string } | null
  remark: string
  stockInDate: Date | null
  agentNumber: string
}

const formatDate = (date: Date | null) => {
  if (!date) {
    return null
  }

  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

const headCell = [
  { id: 1, label: 'STT' },
  { id: 2, label: 'Số hợp đồng' },
  { id: 3, label: 'Mã số đại lý' },
  { id: 4, label: 'Ngày phát hành' },
  { id: 5, label: 'Ngày nhập kho' },
  { id: 6, label: 'Ngày giao hợp đồng' },
  { id: 7, label: 'Tình trạng' },
  { id: 8, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'no' },
  { id: 2, label: 'contractNumber' },
  { id: 3, label: 'agentNumber' },
  { id: 4, label: 'policyIssueDate' },
  { id: 5, label: 'stockInDate' },
  { id: 6, label: 'deliveryDate' },
  { id: 7, label: 'status' },
  { id: 8, label: 'remark' }
]

type StockInExportRow = {
  no: string
  contractNumber: string
  agentNumber: string
  policyIssueDate: string
  stockInDate: string | null
  deliveryDate: string | null
  status: string
  remark: string | null
}

const exportColumns: Array<{ header: string; key: keyof StockInExportRow }> = [
  { header: 'STT', key: 'no' },
  { header: 'Số hợp đồng', key: 'contractNumber' },
  { header: 'Mã số đại lý', key: 'agentNumber' },
  { header: 'Ngày phát hành', key: 'policyIssueDate' },
  { header: 'Ngày nhập kho', key: 'stockInDate' },
  { header: 'Ngày giao hợp đồng', key: 'deliveryDate' },
  { header: 'Tình trạng', key: 'status' },
  { header: 'Ghi chú', key: 'remark' }
]

const useSortStyles = makeStyles({
  icon: {
    '&&': {
      color: '#ED1B2E !important',
      opacity: '1 !important'
    }
  }
})

export default function PDStockInScreen() {
  const sortClasses = useSortStyles()
  const { officeCode } = usePolicyDispatchingOffice()
  const [rows, setRows] = useState<any>([])
  const [contractSortDirection, setContractSortDirection] = useState<'asc' | 'desc' | null>(null)
  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [allRowsSelected, setAllRowsSelected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [openChangeStockInDateDialog, setOpenChangeStockInDateDialog] = useState(false)
  const [changeDialogAction, setChangeDialogAction] = useState<'changeDate' | 'changeOffice'>('changeDate')
  const [title, setTitle] = useState('Thông báo')
  const [message, setMessage] = useState('Vui lòng chọn văn phòng trước khi tìm kiếm.')
  const [pageInfo, setPageInfo] = useState<StockInSearchResponse['body']['page'] | null>(null)
  const initialSearchDone = useRef(false)
  const latestSearchValues = useRef<StockInFilterValues | null>(null)
  const today = new Date()
  const { control, handleSubmit, getValues, trigger } = useForm<StockInFilterValues>({
    defaultValues: {
      issueDate: today,
      contractNumber: '',
      status: { value: '', label: 'Tất cả' },
      remark: '',
      stockInDate: null,
      agentNumber: ''
    }
  })

  const cloneFilterValues = (values: StockInFilterValues): StockInFilterValues => ({
    ...values,
    status: values.status ? { ...values.status } : null
  })

  const hasStockInSearchBody = (response: unknown): response is StockInSearchResponse => {
    return Boolean(
      response &&
        typeof response === 'object' &&
        'body' in response &&
        (response as StockInSearchResponse).body &&
        Array.isArray((response as StockInSearchResponse).body.items) &&
        (response as StockInSearchResponse).body.page
    )
  }

  const getStockInSearchResponse = (response: unknown): StockInSearchResponse | null => {
    const data =
      response && typeof response === 'object' && '_tag' in response && (response as any)._tag === 'Right'
        ? (response as any).right
        : response

    return hasStockInSearchBody(data) ? data : null
  }

  const buildSearchPayload = (
    values: StockInFilterValues,
    nextPage: number,
    isDownload = false
  ): PolicyDispatchingService.StockInSearchPayload => ({
    ...values,
    contractNumber: values.contractNumber
      .split(',')
      .map((contractNumber) => contractNumber.trim())
      .filter(Boolean)
      .join(','),
    status: values.status?.value,
    issueDate: formatDate(values.issueDate) || '',
    stockInDate: formatDate(values.stockInDate),
    officeCode,
    page: nextPage,
    isDownload
  })

  useEffect(() => {
    if (officeCode && !initialSearchDone.current) {
      initialSearchDone.current = true
      search(getValues(), 0, true)
    }
  }, [officeCode])

  const search = (values: StockInFilterValues, nextPage: number, resetSelection = false) => {
    if (!officeCode) {
      setOpenModal(true)
      setTitle('Văn phòng')
      setMessage('Vui lòng chọn văn phòng trước khi tìm kiếm.')
      return
    }

    setIsLoading(true)
    const payload = buildSearchPayload(values, nextPage)

    pipe(
      PolicyDispatchingService.searchStockIn(payload),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!('body' in response)) {
            setRows([])
            setSelectedRows([])
            setAllRowsSelected(false)
            setPageInfo(null)
            setIsLoading(false)
            return
          }

          latestSearchValues.current = cloneFilterValues(values)
          const preserveAllSelected = !resetSelection && allRowsSelected
          const selectedKeys = new Set((resetSelection ? [] : selectedRows).map((row) => row.contractNumber))
          setRows(
            (response.body.items || []).map((item: StockInSearchItem, index: number) => ({
              no: String(response.body.page.page * response.body.page.size + index + 1),
              contractNumber: item.contractNumber,
              agentNumber: item.agentNumber,
              policyIssueDate: item.policyIssueDate,
              stockInDate: item.stockInDate || null,
              branch: item.branch,
              deliveryDate: item.deliveryDate,
              status: getStatusLabel(item.status),
              remark: item.remark || null,
              checked: preserveAllSelected || selectedKeys.has(item.contractNumber)
            }))
          )
          if (resetSelection) {
            setSelectedRows([])
            setAllRowsSelected(false)
          }
          setPageInfo(response.body.page)
          setIsLoading(false)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search policy dispatching stock-in records', error)
          setIsLoading(false)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => search(values, 0, true))

  const handleDownload = async () => {
    if (!officeCode) {
      setOpenModal(true)
      setTitle('Văn phòng')
      setMessage('Vui lòng chọn văn phòng trước khi tải về.')
      return
    }

    if (!(await trigger())) {
      return
    }

    setIsLoading(true)
    try {
      const values = latestSearchValues.current || getValues()
      const firstResponse = await pipe(
        PolicyDispatchingService.searchStockIn(buildSearchPayload(values, 0, true)),
        ZIO.runPromise({})
      )
      const firstSearchResponse = getStockInSearchResponse(firstResponse)
      if (!firstSearchResponse || firstSearchResponse.body.items.length === 0) {
        setOpenModal(true)
        setTitle('Tải về')
        setMessage('Không có dữ liệu để tải về.')
        return
      }

      const remainingResponses = await Promise.all(
        Array.from({ length: Math.max(firstSearchResponse.body.page.totalPages - 1, 0) }, (_, index) =>
          pipe(PolicyDispatchingService.searchStockIn(buildSearchPayload(values, index + 1, true)), ZIO.runPromise({}))
        )
      )
      const items = [firstSearchResponse, ...remainingResponses].flatMap((response) => {
        const searchResponse = getStockInSearchResponse(response)
        return searchResponse ? searchResponse.body.items : []
      })
      if (items.length === 0) {
        setOpenModal(true)
        setTitle('Tải về')
        setMessage('Không có dữ liệu để tải về.')
        return
      }
      const rowsToExport: StockInExportRow[] = items.map((item, index) => ({
        no: String(index + 1),
        contractNumber: item.contractNumber,
        agentNumber: item.agentNumber,
        policyIssueDate: item.policyIssueDate,
        stockInDate: item.stockInDate || null,
        deliveryDate: item.deliveryDate,
        status: getStatusLabel(item.status),
        remark: item.remark || null
      }))

      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Stock In')
      exportColumns.forEach((column, index) => {
        const headerCell = ws.getCell(1, index + 1)
        headerCell.value = column.header
        headerCell.alignment = {
          wrapText: true,
          horizontal: 'left',
          vertical: 'middle'
        }
        headerCell.font = { bold: true }
      })
      rowsToExport.forEach((row, rowIndex) => {
        exportColumns.forEach((column, columnIndex) => {
          ws.getCell(rowIndex + 2, columnIndex + 1).value = row[column.key]
        })
      })
      ws.columns.forEach((column) => {
        let maxLength = 10
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const valueLength = cell.value ? String(cell.value).length : 0
          if (valueLength > maxLength) {
            maxLength = valueLength
          }
        })
        column.width = Math.min(maxLength + 2, 40)
      })

      const buffer = await wb.xlsx.writeBuffer({ useStyles: true })
      const safeFileDate = (formatDate(values.issueDate) || '').replace(/\//g, '-')
      jsFileDownload(buffer, `Stock_In_${safeFileDate}.xlsx`)
    } catch (error) {
      console.log('Unable to download policy dispatching stock-in records', error)
      setOpenModal(true)
      setTitle('Tải về')
      setMessage('Không thể tải dữ liệu. Vui lòng thử lại.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStockIn = () => {
    const payload: StockInPayload[] = selectedRows.map(({ no, checked, status, ...row }) => ({
      ...row,
      status: getStatusValue(status)
    }))
    const condition = payload.every((item) => item.status !== 'AVAILABLE' && item.status !== 'IN_USED')
    if (!condition) {
      setOpenModal(true)
      setTitle('Nhập kho')
      setMessage(
        'Chỉ có thể nhập kho các hợp đồng đang ở trạng thái "Nhận từ NB" hoặc "Đã trả lại". Vui lòng kiểm tra lại.'
      )
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.stockIn(officeCode, payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setOpenModal(true)
          setTitle('Nhập kho')
          setMessage('Nhập kho thành công')
          setAllRowsSelected(false)
          setSelectedRows([])
          search(getValues(), 0, true)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          setTitle('Nhập kho')
          setMessage('Có lỗi xảy ra. Vui lòng thử lại sau.')
          setOpenModal(true)
          console.error('Unable to stock in selected policies', error)
          setIsLoading(false)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleGiveBack = () => {
    const payload: StockInPayload[] = selectedRows.map(({ no, checked, status, ...row }) => ({
      ...row,
      status: getStatusValue(status)
    }))
    const condition = payload.every((item) => item.status === 'AVAILABLE')
    if (!condition) {
      setOpenModal(true)
      setTitle('Trả lại')
      setMessage('Chỉ có thể trả lại các hợp đồng đang ở trạng thái "Còn trong kho". Vui lòng kiểm tra lại.')
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.giveBack(officeCode, payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setOpenModal(true)
          setTitle('Trả lại')
          setMessage('Trả lại thành công')
          setAllRowsSelected(false)
          setSelectedRows([])
          search(getValues(), 0, true)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          setTitle('Trả lại')
          setMessage('Có lỗi xảy ra. Vui lòng thử lại sau.')
          setOpenModal(true)
          console.error('Unable to stock in selected policies', error)
          setIsLoading(false)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const changePage = async (nextPage: number) => {
    if (!pageInfo || nextPage < 0 || nextPage >= pageInfo.totalPages || !(await trigger())) {
      return
    }

    search(getValues(), nextPage)
  }

  const handleChangeStockInDate = (newStockInDate: Date, remark: string) => {
    const payload: StockInPayload[] = selectedRows.map(({ no, checked, status, stockInDate, ...row }) => ({
      ...row,
      status: getStatusValue(status),
      newStockInDate: formatDate(newStockInDate),
      remark: remark || null
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.changeDate(officeCode, payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setOpenModal(true)
          setTitle('Đổi ngày nhập kho')
          setMessage('Đổi ngày nhập kho thành công')
          setAllRowsSelected(false)
          setSelectedRows([])
          search(getValues(), 0, true)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          setTitle('Đổi ngày nhập kho')
          setMessage('Có lỗi xảy ra. Vui lòng thử lại sau.')
          setOpenModal(true)
          console.error('Unable to change stock in date', error)
          setIsLoading(false)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleChangeOffice = (newOffice: SelectOption, remark: string) => {
    const payload: StockInPayload[] = selectedRows.map(({ no, checked, status, ...row }) => ({
      ...row,
      status: getStatusValue(status),
      newOffice: newOffice.value,
      remark: remark || null
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.changeOffice(officeCode, payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setOpenModal(true)
          setTitle('Đổi văn phòng')
          setMessage('Đổi văn phòng thành công')
          setAllRowsSelected(false)
          setSelectedRows([])
          search(getValues(), 0, true)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          setTitle('Đổi văn phòng')
          setMessage('Có lỗi xảy ra. Vui lòng thử lại sau.')
          setOpenModal(true)
          console.error('Unable to change policy dispatching office', error)
          setIsLoading(false)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const validateChangeStockInDate = () => {
    const condition = selectedRows.every((row) => getStatusValue(row.status) === 'AVAILABLE')
    if (!condition) {
      setOpenModal(true)
      setTitle('Đổi ngày nhập kho')
      setMessage('Chỉ có thể đổi ngày nhập kho các hợp đồng đang ở trạng thái "Còn trong kho". Vui lòng kiểm tra lại.')
      return
    }
    setChangeDialogAction('changeDate')
    setOpenChangeStockInDateDialog(true)
  }

  const validateChangeOffice = () => {
    setChangeDialogAction('changeOffice')
    setOpenChangeStockInDateDialog(true)
  }

  const sortedRows = contractSortDirection
    ? [...rows].sort((firstRow, secondRow) => {
        const comparison = String(firstRow.contractNumber || '').localeCompare(
          String(secondRow.contractNumber || ''),
          undefined,
          { numeric: true }
        )
        return contractSortDirection === 'asc' ? comparison : -comparison
      })
    : rows

  return (
    <View style={styles.container}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={title}
        message={message}
        onClose={() => setOpenModal(false)}
      />
      <PDChangeInfoDialog
        visible={openChangeStockInDateDialog}
        selectedCount={allRowsSelected ? pageInfo?.totalElements || 0 : selectedRows.length}
        currentDates={selectedRows.map((row) => row.stockInDate)}
        action={changeDialogAction}
        onClose={() => setOpenChangeStockInDateDialog(false)}
        onConfirm={(value, remark) => {
          if (changeDialogAction === 'changeDate') {
            handleChangeStockInDate(value as Date, remark)
          } else {
            handleChangeOffice(value as SelectOption, remark)
          }
        }}
      />
      <View>
        <Text style={styles.title}>Quản lý nhập kho</Text>
      </View>
      <PDStockInFilter
        control={control}
        onSearch={handleSearch}
        onStockIn={handleStockIn}
        onGiveBack={handleGiveBack}
        onChangeStockInDate={validateChangeStockInDate}
        onChangeOffice={validateChangeOffice}
        onDownload={handleDownload}
        totalCount={pageInfo?.totalElements || 0}
        selectedCount={allRowsSelected ? pageInfo?.totalElements || 0 : selectedRows.length}
      />
      <View style={styles.tableWrapper}>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={sortedRows}
          renderHeadCell={(cell) =>
            cell.id === 2 ? (
              <TableSortLabel
                active={contractSortDirection !== null}
                direction={contractSortDirection || 'asc'}
                hideSortIcon={false}
                classes={{ icon: sortClasses.icon }}
                title="Sắp xếp theo số hợp đồng"
                onClick={() => setContractSortDirection(contractSortDirection === 'asc' ? 'desc' : 'asc')}
              >
                {cell.label}
              </TableSortLabel>
            ) : (
              cell.label
            )
          }
          hasCheckbox
          hasCheckAll
          renderCell={(cell, value, row, rowIndex) =>
            cell === 'no' ? <Text>{(pageInfo ? pageInfo.page * pageInfo.size : 0) + rowIndex + 1}</Text> : undefined
          }
          getStyle={(cell, value) => (cell === 'status' ? getStatusStyle(value) : {})}
          onSelectionChange={(nextRows, nextSelectedRows) => {
            setRows(nextRows)
            setSelectedRows((currentSelectedRows) => {
              const currentPageKeys = new Set(rows.map((row: any) => row.contractNumber))
              const selectionsFromOtherPages = currentSelectedRows.filter(
                (row) => !currentPageKeys.has(row.contractNumber)
              )
              const mergedSelectedRows = [...selectionsFromOtherPages, ...nextSelectedRows]

              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && mergedSelectedRows.length === pageInfo.totalElements)
              )

              return mergedSelectedRows
            })
          }}
          onSelectAllChange={(selected) => {
            setRows((currentRows: any[]) => currentRows.map((row: any) => ({ ...row, checked: selected })))

            if (!selected) {
              setAllRowsSelected(false)
              setSelectedRows([])
              return
            }

            if (!officeCode || !pageInfo || pageInfo.totalPages <= 1) {
              setAllRowsSelected(true)
              setSelectedRows(rows)
              return
            }

            const values = latestSearchValues.current || getValues()
            setIsLoading(true)
            Promise.all(
              Array.from({ length: pageInfo.totalPages }, (_, page) =>
                pipe(PolicyDispatchingService.searchStockIn(buildSearchPayload(values, page)), ZIO.runPromise({}))
              )
            )
              .then((responses) => {
                const allRows = responses.flatMap((response) => {
                  const searchResponse = getStockInSearchResponse(response)
                  return searchResponse
                    ? searchResponse.body.items.map((item: StockInSearchItem, index: number) => ({
                        no: String(searchResponse.body.page.page * searchResponse.body.page.size + index + 1),
                        contractNumber: item.contractNumber,
                        agentNumber: item.agentNumber,
                        policyIssueDate: item.policyIssueDate,
                        stockInDate: item.stockInDate || null,
                        branch: item.branch,
                        status: getStatusLabel(item.status),
                        remark: item.remark || null,
                        checked: true
                      }))
                    : []
                })
                setAllRowsSelected(true)
                setSelectedRows(allRows.length > 0 ? allRows : rows)
                setIsLoading(false)
              })
              .catch(() => {
                setSelectedRows(rows)
                setIsLoading(false)
                setOpenModal(true)
                setTitle('Chọn tất cả')
                setMessage('Không thể tải toàn bộ danh sách hợp đồng. Đã chọn các hợp đồng ở trang hiện tại.')
              })
          }}
          allRowsSelected={Boolean(allRowsSelected && pageInfo && pageInfo.totalElements > 0)}
          pagination={
            pageInfo
              ? {
                  ...pageInfo,
                  onPageChange: changePage
                }
              : undefined
          }
        />
        {isLoading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color="#ED1B2E" />
          </View>
        )}
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  status: {
    paddingTop: 4,
    paddingRight: 10,
    paddingBottom: 4,
    paddingLeft: 10,
    borderRadius: '999px',
    fontWeight: 'bold',
    fontSize: '12px',
    width: 'fit-content',
    textAlign: 'center'
  },
  tableWrapper: {
    position: 'relative' as const,
    marginTop: 30
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
